import { inject, injectable } from "inversify";
import "reflect-metadata";
import ToastService from "src/services/toast.service";
import BlockchainService from "../../services/blockchain.service";
import HttpService from "../../services/http.service";
import IdentityService from "../../services/identity.service";
import {ArtworkService, EditionDto} from "../../services/openapi/generated";
import { IDetailedArt, IGalleryItem, IHttpCollectionResult, INew } from "../../typings";

@injectable()
export default class ArtsDataService {
    @inject(HttpService)
    public httpService: HttpService;

    @inject(IdentityService)
    public identityService: IdentityService;

    @inject(BlockchainService)
    public blockchainService: BlockchainService;

    @inject(ToastService)
    public toastService: ToastService;

    public collectionInndexes: number[] = [];

    public canEdit(tokenId: any, userEmail: string) {
        return true;
    }

    // only show owned by you
    public getCollectionItems(
        pageSize: number,
        pageNumber: number,
        rarity?: number,
        text?: string,
    ): Promise<IHttpCollectionResult<IGalleryItem>> {
        const headers = {} as any;

        if (this.identityService.getToken() != null) {
            headers.Authorization = `Bearer ${this.identityService.getToken()}`;
        }

        return new Promise<IHttpCollectionResult<IGalleryItem>>((resolve, reject) => {
            ArtworkService.artworkControllerGetCollectorPieces(
                pageNumber,
                pageSize,
                rarity ? rarity.toString() : undefined,
                text).then((result) => {
                    this.collectionInndexes = (result.items).map((item) => item.id);
                    const galleryItems = result.items.map((item) => ({
                        id: item.id,
                        image: item.image,
                        description: item.description_module.description,
                        rarity: item.rarity,
                        count: item.editions.length,
                        editionTypeCount: item.editions.length,
                        title: item.name,
                        public_edition_number: 0,
                        maxNumberOfEditions: item.maxNumberOfEditions,
                        edition_number: 0,
                        editionType: EditionDto.type.LIMITED_EDITION,
                        blockchain_hash: item.contractAddress,
                        pages: 0,
                        owner_wallet: "",
                        ownedEditions: [],
                        isCopy: false,
                        // added
                        unconfirmed: false,
                        artistId: item.artistId,
                        artistName: item.artistName,
                        ownerId: 0,
                    }));
                    resolve({ results: galleryItems as any, count: result.count, next: "", previous: "" });
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public getNeighborsInCollection(index) {
        const result: any[] = [undefined, undefined];

        for (let i = 0; i < this.collectionInndexes.length; i++) {
            if (this.collectionInndexes[i] === index) {
                if (i > 0) {
                    result[0] = this.collectionInndexes[i - 1];
                } else {
                    result[1] = this.collectionInndexes[this.collectionInndexes.length - 1];
                }
                if (i < this.collectionInndexes.length - 1) {
                    result[1] = this.collectionInndexes[i + 1];
                } else {
                    result[1] = this.collectionInndexes[0];
                }
            }
        }

        return result;
    }

    public getGalleryItems(
        pageSize: number,
        pageNumber: number,
        rarity?: number,
        text?: string,
    ): Promise<IHttpCollectionResult<IGalleryItem>> {
        let id = 0;
        if (this.identityService.userInfo) {  id = this.identityService.userInfo.id; }
        return new Promise<IHttpCollectionResult<IGalleryItem>>((resolve, reject) => {
            ArtworkService.artworkControllerGetArtworkPage(
                pageNumber,
                pageSize,
                undefined,
                rarity ? rarity.toString() : undefined,
                text).then((result) => {
                    resolve({ results: result as any, count: (result as any).length } as any);
                    this.collectionInndexes = (result as any).map((item) => item.id);
                }).catch((error) => {
                    reject(error);
                });
        });
    }

    public getToken(id: any, edition?: any): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            ArtworkService.artworkControllerGetArtworkDetails(id).then((result) => {
                resolve(result);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public editToken(id: any, data: Partial<IDetailedArt>): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            // @ts-ignore
            ArtworkService.artworkControllerUpdateArtwork({
                ...data,
                location: data.mold_creation_place,
                image: typeof data.image === "string" ? data.image : undefined,
                artworkId: Number(id),
            }).then((result) => {
                resolve(result);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public contactArtist(full_name, email, art_token, text): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            ArtworkService.artworkControllerContactArtist({
                art_token,
                email,
                full_name,
                text,
            }).then((result) => {
                resolve(result);
            }).catch((erro) => {
                reject(erro);
            });
        });
    }

    public sendToken(artworkId, edition, recipientAddress, password): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            ArtworkService.artworkControllerTransferArtwork({
                artworkId,
                edition: parseInt(edition),
                receiver: recipientAddress,
                password,
            }).then((result) => {
                resolve(result);
            }).catch((erro) => {
                reject(erro);
            });
        });
    }

    public certifyToken(
        art_token_photo,
        artist,
        prove_of_purchase,
        signature_photo,
        card_token: string,
        user,
        dimensions: string,
    ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.httpService
                .post(
                    "arttokens/get_certificate/",
                    {
                        art_token_photo,
                        artist,
                        user,
                        prove_of_purchase,
                        signature_photo,
                        card_token,
                        dimensions,
                    },
                    {
                        Authorization: `Bearer ${this.identityService.getToken()}`,
                    },
                )
                .then((result) => {
                    resolve(result as any);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public cloneToken(artworkId: any, type: string, uid: string, tah: string, publicEditionNumber: number): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            ArtworkService.artworkControllerCloneArtwork({
                type,
                artworkId,
                uid,
                tah,
                publicEditionNumber,
            }).then((result) => {
                resolve(result as any);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public createToken(
        title: string,
        date: string,
        editionCount: number,
        rarity: any,
        dimensions?: string,
        medium?: string,
        created_in?: string,
        description?: string,
        videoLink?: string,
    ): Promise<{ id: number }> {
        /*
        'medium', 'mold_creation_place', 'limited_edition', 'dimensions'
        */
        return new Promise<{ id: number }>((resolve, reject) => {
            ArtworkService.artworkControllerNewArtwork({
                creationDate: Number(date),
                description: description || "",
                dimensions: dimensions || "" ,
                location: created_in || "",
                maxNumberOfEditions: parseInt(String(editionCount)),
                medium: medium || "",
                name: title,
                rarity: rarity || "",
                videoLink: videoLink || "",
            }).then((result) => {
                resolve(result as any);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public async getIsFavourite(id: string) {
        try {
            await ArtworkService.artworkControllerGetFavorite(Number(id));
            return true;
        } catch (e) {
            return false;
        }
    }

    public async delete(id: string) {
        return this.httpService.delete(
            `arttokens/delete/${id}`,
            {},
            {
                Authorization: `Bearer ${this.identityService.getToken()}`,
            },
        );
    }

    public async setFavourite(id: string) {
        await this.httpService.post(
            `artwork/favorite`,
            { artworkId: id },
            {
                Authorization: `Bearer ${this.identityService.getToken()}`,
            },
        );
    }

    public async disableFavourite(id: string) {
        await this.httpService.post(
            `artwork/unfavorite`,
            { artworkId: id },
            {
                Authorization: `Bearer ${this.identityService.getToken()}`,
            },
        );
    }

    public discriminateEditionType(type: string) {
        switch (type) {
            case "limited":
            case "normal":
                return "Limited edition";
            case "ap":
                return "AP";
            case "hu":
                return "HU";
        }

        return type;
    }

    public getApMaximum(editionCount: number) {
        if (editionCount >= 1 && editionCount <= 5) {
            return 1;
        }
        if (editionCount >= 6 && editionCount <= 7) {
            return 2;
        }
        if (editionCount >= 8 && editionCount <= 24) {
            return 3;
        }
        if (editionCount >= 25 && editionCount <= 95) {
            return 5;
        }
        if (editionCount >= 95 && editionCount <= 98) {
            return 10;
        }

        if (editionCount >= 99) {
            return 15;
        }

        return 0;
    }

    public async getNews(): Promise<INew[]> {
        const data = await this.httpService.get<{ count: number; news: INew[] }>(`news/?count=${4}`);
        return data.news;

        /*return [
            {
                author: 'Javier Cabrera',
                title: 'Mock 1',
                thumbnail: '/static/images/test/brain.png',
                link: 'http://www.google.com',
                description: 'Un chino cayo en un pozo las trupas se hicieron agua'
            },
            {
                author: 'Javier Cabrera',
                title: 'Mock 1',
                thumbnail: '/static/images/test/brain.png',
                link: 'http://www.google.com',
                description: 'Un chino cayo en un pozo las trupas se hicieron agua'
            },
            {
                author: 'Javier Cabrera',
                title: 'Mock 1',
                thumbnail: '/static/images/test/brain.png',
                link: 'http://www.google.com',
                description: 'Un chino cayo en un pozo las trupas se hicieron agua'
            },

            {
                author: 'Javier Cabrera',
                title: 'Mock 1',
                thumbnail: '/static/images/test/brain.png',
                link: 'http://www.google.com',
                description: 'Un chino cayo en un pozo las trupas se hicieron agua'
            }
        ];*/
    }

    public async setPrice(price: number, artTitle: string | undefined): Promise<void> {
        if (!artTitle) {
            this.toastService.showToast(
                "Something went wrong",
                "",
                "error",
            );
        } else {
            this.toastService.showToast(
                `Price of ${artTitle} set to ${price.toString()} credits`,
                "",
                "success",
            );
        }
    }

    public async getArtworkCreationCost(): Promise<number> {
        const res = await ArtworkService.artworkControllerGetArtworkCreationCost();
        return res.cost;
    }
}



// WEBPACK FOOTER //
// ./src/components/arts/arts.data.service.ts